import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { BaseExpandableAgGridComponent } from '../../../../UI-Lib/Expandable-Component/baseExpandableAgGridComponent';
import { ValuationTemplateService } from '../../valuationTemplate.service';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../../../Compliance/AgGrid';
import { IMutexServiceHandler, WeissmanMutexService } from '../../../../Compliance/WeissmanMutexService';
import { WeissmanModalService } from '../../../../Compliance/WeissmanModalService';
import { AddStateComponent, AddStateModalParams } from './Add-State/addState.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from '../../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { AgGridMultiSelectCellRendererParams, AgGridMultiSelectedCellRenderer, AgGridMultiSelectedHeaderRenderer, AgGridMultiSelectHeaderRendererParams, AgGridMultiSelectTracker } from '../../../../Compliance/AgGrid/MultiSelectTracker';
import { AgGridActionCellRendererComponent, AgGridActionCellRendererParams } from '../../../../Compliance/AgGrid/CellRenderers/agGridActionCellRenderer.component';
import { BusyIndicatorService } from '../../../../Busy-Indicator';

@Component({
    selector: 'valuation-states-list',
    templateUrl: './valuationStatesList.component.html',
    standalone: false
})
export class ValuationStatesListComponent extends BaseExpandableAgGridComponent implements OnInit, OnDestroy, IMutexServiceHandler {
    constructor(
        private readonly _valuationTemplateService: ValuationTemplateService,
        private readonly _modalService: WeissmanModalService,
        private readonly _mutexService: WeissmanMutexService,
        private readonly _busyIndicatorService: BusyIndicatorService
    ) {
        super(_valuationTemplateService, 'valuation-states-list');
    }

    gridOptions = new AgGridOptionsBuilder({
        rowClassRules: {
            'ag-row-selected': (params) => {
                const data = params.data as Core.ValuationTemplateStateModel;
                return data && this._gridTracker.isRowSelected(data.stateId);
            }
        },
        onFilterChanged: () => this._gridTracker.onGridFilterChanged(),
        onSortChanged: () => this._gridTracker.onGridSortChanged()
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withFloatingFilter()
        .withMultipleColumnSort()
        .withTextSelection()
        .build();

    template: Core.ValuationTemplateModel;
    canEdit: boolean = true;
    canEnterEditMode: boolean;
    editMode: boolean;
    hasSelectedRows: boolean;
    gridApi: GridApi;

    private _gridTracker: AgGridMultiSelectTracker;
    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this._valuationTemplateService.template$
            .pipe(takeUntil(this._destroy$))
            .subscribe(x => {
                if (x) {
                    this.template = x;
                    this.canEnterEditMode = x.isInDevelopment;
                    this._setGridData();

                    if (!x.isInDevelopment && this.editMode) {
                        this.cancel();
                    }
                }
            });

        this._mutexService.lockChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(x => this.canEnterEditMode = !x[this._valuationTemplateService.editGroup] && this.template.isInDevelopment);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._mutexService.release(this, this._valuationTemplateService.editGroup);
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this._gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));

        this._gridTracker.selectedRows$
            .pipe(takeUntil(this._destroy$))
            .subscribe(x => this.hasSelectedRows = x.selectAllRows || !!(!x.selectAllRows && x.selectedRows.length));

        super.setGridApi(event.api);

        const columns: ColDef[] = [
            {
                field: 'stateId',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressMovable: true,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                hide: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: {
                    tracker: this._gridTracker
                } as AgGridMultiSelectHeaderRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this._gridTracker
                } as AgGridMultiSelectCellRendererParams
            },
            {
                headerName: 'State',
                field: 'stateFullName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => '',
                    newWindow: true,
                    isHelpDisabled: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const entry = params.data as Core.ValuationTemplateStateModel;
                        if (!entry) {
                            return '';
                        }
                        return `#/states/${entry.stateId}`;
                    }
                } as AgGridLinkCellRendererParams
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                hide: true,
                cellRenderer: AgGridActionCellRendererComponent,
                cellRendererParams: {
                    buttonConfigs: [
                        {
                            iconClass: 'fa-trash',
                            onClick: this._onRemove.bind(this),
                            isShown: () => true,
                            buttonClass: 'btn-primary btn-danger',
                            helpContentId: 'app.delete'
                        }
                    ]
                } as AgGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'stateFullName', sort: 'asc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setGridData();
    }

    async addNewState(): Promise<void> {
        const params = {
            valuationTemplateId: this.template.valuationTemplateId,
            statesSelected: this.template.states.map(x => x.stateId)
        } as AddStateModalParams;

        await this._modalService.showAsync(AddStateComponent, params, 'modal-md');
    }

    edit(): void {
        if (!this.canEdit) {
            return;
        }

        this._toggleGridEdit(true);
    }

    cancel(): void {
        this._toggleGridEdit(false);
    }

    wsMutexRelease(groupId: string): Promise<void> {
        return Promise.resolve();
    }

    async bulkRemove(): Promise<void> {
        const rowModel = this._gridTracker.getSelectedRowsModel();
        if (!this.hasSelectedRows) {
            return Promise.resolve();
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Removing States' });

        try {
            const states = this.template.states.filter(x => {
                if (rowModel.selectAllRows) {
                    return rowModel.selectedRows.find(y => y === x.stateId);
                } else {
                    return !rowModel.selectedRows.find(y => y === x.stateId);
                }
            });
            await this._valuationTemplateService.updateTemplateStates(this.template.valuationTemplateId, states);
        } finally {
            this._gridTracker.clear();
            busyRef.hide();
        }
    }

    private _setGridData(): void {
        if (!this.gridApi) {
            return;
        }
        this.gridApi.setGridOption('rowData', this.template.states);
        this.gridApi.sizeColumnsToFit();
    }

    private _toggleGridEdit(editing: boolean): void {
        this.editMode = editing;
        if (this.gridOptions && this.gridApi) {
            this.gridApi.setColumnsVisible(['stateId', 'actions'], this.editMode);
            this.gridApi.sizeColumnsToFit();
        }
        if (!this.editMode) {
            this._mutexService.release(this, this._valuationTemplateService.editGroup);
            this._gridTracker.clear();
        } else {
            this._mutexService.acquire(this, this._valuationTemplateService.editGroup);
        }
        this.gridApi.redrawRows();
    }

    private async _onRemove(params: AgGridActionCellRendererParams): Promise<void> {
        const state = params.data as Core.ValuationTemplateStateModel;
        if (!state) {
            return;
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Removing State' });

        try {
            const states = this.template.states.filter(x => x.stateId !== state.stateId);
            await this._valuationTemplateService.updateTemplateStates(this.template.valuationTemplateId, states);
        } finally {
            busyRef.hide();
        }
    }

    private _getGridRowIds(skip, take): Compliance.QueryResultModel<number> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort(x => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        return {
            data: rows.map((x) => {
                const assessor = x.data as Core.ValuationTemplateStateModel;
                return assessor && assessor.stateId;
            })
        } as Compliance.QueryResultModel<number>;
    }
}
